import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { settingsAPI } from '../../services/settingsAPI';
import { toast } from 'react-hot-toast';

const InlineUrlEditor = ({ settingKey, defaultValue, children, className = "", iconComponent = null }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  const originalValue = useRef('');

  // Initialize value when component mounts or children changes
  useEffect(() => {
    setValue(children || defaultValue);
    originalValue.current = children || defaultValue;
  }, [children, defaultValue]);

  const handleDoubleClick = () => {
    if (user?.role === 'admin') {
      setIsEditing(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Token d\'authentification manquant');
        return;
      }

      await settingsAPI.update(settingKey, value, token);
      
      // Dispatch custom event for real-time updates
      window.dispatchEvent(new CustomEvent('settingUpdated', { 
        detail: { key: settingKey, value: value } 
      }));
      
      setIsEditing(false);
      toast.success('Lien mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du lien:', error);
      toast.error('Erreur lors de la mise à jour du lien');
      setValue(originalValue.current);
    }
  };

  const handleCancel = () => {
    setValue(originalValue.current);
    setIsEditing(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <span 
        className={`cursor-pointer hover:text-yellow-400 transition-colors ${className}`}
        onDoubleClick={handleDoubleClick}
        onClick={() => {
          if (children && children !== '#') {
            window.open(children, '_blank');
          }
        }}
        title="Double-cliquez pour modifier le lien (Admin) - Clic pour ouvrir"
      >
        {iconComponent || children || defaultValue}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      type="url"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="bg-white text-gray-900 px-2 py-1 rounded border border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 min-w-[200px]"
      placeholder="Entrez l'URL..."
    />
  );
};

export default InlineUrlEditor;
